import { useState } from 'react';
import { ContentCard } from '../../components/ContentCard/ContentCard';
import { Modal } from '../../components/Modal/Modal';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { Res, useCms } from '../../state/api';
import { DeliveryTypeCompany, DeliveryTypePrivate } from '../../types';
import { close, delivery, house_2, Icon, info, store_front, truck } from '../Icon/Icon';
import { Loader } from '../Loader/Loader';
import { CmsComponent } from '../cms/CmsComponent';
import './DeliveryInput.css';
import { RadioButton } from './RadioButton';

export interface DeliveryModel {
	type: DeliveryTypeCompany | DeliveryTypePrivate;
	notification: 'call' | 'confer' | '';
}

type DeliverySummaryProps = {
	model: DeliveryModel;
};
export function DeliverySummary({ model }: DeliverySummaryProps): JSX.Element {
	const t = useTranslation().form;
	switch (model.type) {
		case 'door_delivery':
			return <>{t.door}</>;
		case 'servicepoint':
			return <>{t.servicepoint}</>;
		case 'home':
			return <>{t.home}</>;
	}
}

type DeliveryInputProps = {
	model: DeliveryModel;
	hideDoor?: boolean;
	homeDeliveryEnabled?: boolean;
	onChange: (model: DeliveryModel) => void;
	customerType: 'private' | 'company' | '';
	renderedInCheckout?: boolean;
};
export function DeliveryInput({
	model,
	onChange,
	hideDoor,
	homeDeliveryEnabled,
	customerType,
}: DeliveryInputProps): JSX.Element {
	const t = useTranslation().form;
	const t2 = useTranslation().checkout;
	const config = useConfig();
	const isSwedishStore = config.store === 'kgse';
	// const isDanishStore = config.store === 'kgdk';
	const isGermanStore = config.store === 'kgde';
	const deliveryIcon = isGermanStore ? delivery : store_front;

	return (
		<>
			{!isGermanStore && !hideDoor && (
				<RadioButton
					group="delivery_method"
					value="door_delivery"
					checked={'door_delivery' === model.type}
					onChange={(e) =>
						onChange({
							...model,
							type: e.target.value as DeliveryTypeCompany,
						})
					}
				>
					<Icon shape={truck} />
					<span className="ml-2">{t.door}</span>
				</RadioButton>
			)}
			{model.type === 'door_delivery' && isSwedishStore && !hideDoor && (
				<label className="delivery-notice">
					<span>
						<input
							type="checkbox"
							checked={model.notification === 'call' ? true : false}
							onChange={(e) =>
								onChange({
									...model,
									notification: e.target.checked ? 'call' : '',
								})
							}
						/>
						<span className="delivery-notice-text">{t.giveNotice}</span>
					</span>
				</label>
			)}
			<RadioButton
				group="delivery_method"
				value="servicepoint"
				checked={'servicepoint' === model.type}
				onChange={(e) =>
					onChange({
						...model,
						type: e.target.value as DeliveryTypeCompany,
						notification: '',
					})
				}
			>
				<Icon shape={deliveryIcon} />
				<span className="ml-2">{t.servicepoint}</span>
			</RadioButton>

			{isSwedishStore && (
				<>
					<div className="delivery-home-option">
						<RadioButton
							className="radio-with-help"
							group="delivery_method"
							value="home"
							checked={'home' === model.type}
							disabled={!homeDeliveryEnabled}
							onChange={(e) =>
								onChange({
									...model,
									type: e.target.value as DeliveryTypeCompany,
									notification: '',
								})
							}
						>
							<Icon shape={house_2} />
							<span className="ml-2">{customerType === 'company' ? t.home : t.homePrivate}</span>
						</RadioButton>
						<DeliveryDescription homeDeliveryEnabled={homeDeliveryEnabled} />
					</div>
					{!homeDeliveryEnabled && (
						<div className="delivery-disabled-message">
							<p>{t2.deliveryDisabledMessage}</p>
						</div>
					)}
				</>
			)}
		</>
	);
}

type Preventable = { preventDefault(): void };
function preventDefault<T extends Preventable>(handler: (e: T) => void): (e: T) => void {
	return function (e: T) {
		e.preventDefault();
		return handler(e);
	};
}

const DeliveryDescription = ({ homeDeliveryEnabled }: { homeDeliveryEnabled?: boolean }) => {
	const [showHelp, setShowHelp] = useState(false);

	return (
		<>
			{!showHelp && (
				<button
					className={`delivery-description-icon ${!homeDeliveryEnabled ? 'home-delivery-message' : ''}`}
					onClick={preventDefault(() => setShowHelp(!showHelp))}
				>
					<Icon shape={info} />
				</button>
			)}
			<HomeDeliveryInfoModal show={showHelp} handleClose={() => setShowHelp(false)} />
		</>
	);
};

const HomeDeliveryInfoModal = ({ show, handleClose }: { show: boolean; handleClose: () => void }): JSX.Element => {
	const { data } = useCms(Res.cmsComponent, { slug: 'home-delivery-description' });
	return (
		<>
			<Modal className="content-card-modal" show={show} onDismiss={() => handleClose()}>
				<ContentCard className="content">
					<a className="discard" onClick={() => handleClose()}>
						<Icon shape={close} />
					</a>
					{data ? <CmsComponent structure={data} /> : <Loader />}
				</ContentCard>
			</Modal>
		</>
	);
};
